import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { LOGOS, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const ssoLogin = async () => {
    try { const { data } = await api.get("/auth/microsoft/login"); window.location.href = data.auth_url; }
    catch { toast.error("SSO unavailable"); }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left hero */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden bg-gradient-to-br from-[#1c4a86] via-cc-blue to-[#10325a]">
        {/* soft depth glows (no busy photo, keeps the logo crisp) */}
        <div className="absolute -top-24 -left-24 w-[28rem] h-[28rem] rounded-full bg-white/10 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[24rem] h-[24rem] rounded-full bg-cyan-300/10 blur-3xl" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex justify-center pt-6">
            <div className="bg-white rounded-3xl px-8 py-7 shadow-2xl shadow-black/25 ring-1 ring-black/5 max-w-md">
              <img src={LOGOS.campus} alt="Campus Connections" className="h-24 w-full object-contain" />
            </div>
          </div>
          <div>
            <h1 className="text-4xl font-semibold leading-tight">Campus Connections</h1>
            <p className="mt-4 text-white/85 max-w-md">Manage student placements, facility acceptance, paperwork clearance, video appointments and recruiting — all in one place.</p>
          </div>
          <img src={LOGOS.tmc} alt="TMC" className="h-12 w-fit opacity-90" />
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-[#FAFAFA]">
        <div className="w-full max-w-sm">
          <img src={LOGOS.campus} alt="Campus Connections" className="h-16 w-auto mx-auto mb-6 object-contain lg:hidden" />
          <h2 className="text-2xl font-semibold text-zinc-900">Welcome back</h2>
          <p className="text-sm text-zinc-500 mt-1">Sign in to continue to Campus Connections.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-cc-blue hover:bg-cc-blue-dark text-white">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Sign in
            </Button>
          </form>

          <div className="my-4 flex items-center gap-3 text-xs text-zinc-400">
            <div className="h-px flex-1 bg-border" /> OR <div className="h-px flex-1 bg-border" />
          </div>
          <Button variant="outline" onClick={ssoLogin} data-testid="sso-login" className="w-full">
            Sign in with Microsoft (TMC)
          </Button>

          <p className="text-xs text-center mt-5 text-zinc-400">
            Accounts are created by your Campus Coordinator. Need access? Contact Campus Relations.
          </p>

        </div>
      </div>
    </div>
  );
}
